import { Button } from '@bambi/component-library/src/components/Button';
import { DateTime } from 'luxon';
import { useCallback, useState, type ComponentType } from 'react';

import { type NotificationRendererProps } from './renderers/types';
import { useAppDispatch } from 'src/app/store/hooks';
import {
  useNotificationsNotificationsPartialUpdateMutation,
  type NotificationRead,
} from 'src/common/external/bambi-api/notificationsApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

interface NotificationItemProps {
  title: string;
  notification: NotificationRead;
  Renderer: ComponentType<NotificationRendererProps>;
}

export function NotificationItem({
  title,
  notification,
  Renderer,
}: NotificationItemProps) {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);

  const handleToggleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, [setOpen]);

  const [trigger] = useNotificationsNotificationsPartialUpdateMutation();

  const handleMarkAsRead = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        await trigger({
          id: notification.id,
          patchedNotification: {
            read_at: DateTime.now().toISO(),
          },
        }).unwrap();
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to mark notification as read',
            description: formatServerError(e),
          })
        );
      }
    },
    [trigger, notification, dispatch]
  );

  return (
    <div
      className="flex cursor-pointer flex-row justify-between gap-1"
      aria-label="Click to expand or collapse notification"
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={handleToggleOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggleOpen();
        }
      }}
    >
      <div className="flex flex-col gap-1">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-slate-400">
          {DateTime.fromISO(notification.created_at).toRelative()}
        </div>
        {open ? <Renderer notification={notification} /> : null}
      </div>
      <Button variant="anchor-inline" onClick={handleMarkAsRead}>
        Mark Read
      </Button>
    </div>
  );
}
